import React, { useState } from 'react';
import { X, AlertTriangle, FileX, DollarSign, XCircle, Wallet, Building, PhoneOff, Check, Ban, Users, FlaskConical, Briefcase, UserX } from 'lucide-react';

// Razones predefinidas para pérdida de negocios con íconos
const LOSS_REASONS = [
  { id: 'no_response', label: 'Cliente no responde', icon: PhoneOff, color: 'orange' },
  { id: 'incomplete_docs', label: 'Documentación incompleta', icon: FileX, color: 'amber' },
  { id: 'cheaper_price', label: 'Precio de la oferta', icon: DollarSign, color: 'blue' },
  { id: 'project_cancelled', label: 'No van a continuar con el proyecto', icon: XCircle, color: 'red' },
  { id: 'no_budget', label: 'No tienen presupuesto', icon: Wallet, color: 'purple' },
  { id: 'internal_solution', label: 'Van a hacerlo internamente', icon: Building, color: 'slate' },
  { id: 'other_provider', label: 'Se fueron con otro proveedor', icon: UserX, color: 'pink' },
  { id: 'spam', label: 'Spam', icon: Ban, color: 'rose' },
  { id: 'business_partner', label: 'Buscaban Socios Comercial', icon: Users, color: 'teal' },
  { id: 'internal_test', label: 'Prueba interna', icon: FlaskConical, color: 'cyan' },
  { id: 'job_seeker', label: 'Busca empleo', icon: Briefcase, color: 'indigo' },
];

const colorClasses = {
  orange: {
    bg: 'bg-orange-50',
    border: 'border-orange-500',
    icon: 'text-orange-600 bg-orange-100',
    text: 'text-orange-700'
  },
  amber: {
    bg: 'bg-amber-50',
    border: 'border-amber-500',
    icon: 'text-amber-600 bg-amber-100',
    text: 'text-amber-700'
  },
  blue: {
    bg: 'bg-blue-50',
    border: 'border-blue-500',
    icon: 'text-blue-600 bg-blue-100',
    text: 'text-blue-700'
  },
  red: {
    bg: 'bg-red-50',
    border: 'border-red-500',
    icon: 'text-red-600 bg-red-100',
    text: 'text-red-700'
  },
  purple: {
    bg: 'bg-purple-50',
    border: 'border-purple-500',
    icon: 'text-purple-600 bg-purple-100',
    text: 'text-purple-700'
  },
  slate: {
    bg: 'bg-slate-50',
    border: 'border-slate-500',
    icon: 'text-slate-600 bg-slate-100',
    text: 'text-slate-700'
  },
  rose: {
    bg: 'bg-rose-50',
    border: 'border-rose-500',
    icon: 'text-rose-600 bg-rose-100',
    text: 'text-rose-700'
  },
  pink: {
    bg: 'bg-pink-50',
    border: 'border-pink-500',
    icon: 'text-pink-600 bg-pink-100',
    text: 'text-pink-700'
  },
  teal: {
    bg: 'bg-teal-50',
    border: 'border-teal-500',
    icon: 'text-teal-600 bg-teal-100',
    text: 'text-teal-700'
  },
  cyan: {
    bg: 'bg-cyan-50',
    border: 'border-cyan-500',
    icon: 'text-cyan-600 bg-cyan-100',
    text: 'text-cyan-700'
  },
  indigo: {
    bg: 'bg-indigo-50',
    border: 'border-indigo-500',
    icon: 'text-indigo-600 bg-indigo-100',
    text: 'text-indigo-700'
  }
};

const LossReasonModal = ({ isOpen, onClose, onConfirm, leadName = '' }) => {
  const [selectedReason, setSelectedReason] = useState('');
  const [customReason, setCustomReason] = useState('');

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (!selectedReason) {
      return; // Button is disabled anyway
    }
    
    const reasonLabel = LOSS_REASONS.find(r => r.id === selectedReason)?.label || selectedReason;
    onConfirm({
      reasonId: selectedReason,
      reasonLabel: reasonLabel,
      customNote: customReason.trim() || null
    });
    
    // Reset state
    setSelectedReason('');
    setCustomReason('');
  };

  const handleCancel = () => {
    setSelectedReason('');
    setCustomReason('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60]" data-testid="loss-reason-modal" onClick={(e) => e.stopPropagation()}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden transform transition-all max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-red-500 to-rose-600 px-6 py-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-white/20 rounded-xl">
                <AlertTriangle className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Razón de Pérdida</h2>
                {leadName && (
                  <p className="text-sm text-white/80">{leadName}</p>
                )}
              </div>
            </div>
            <button 
              onClick={handleCancel}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          <p className="text-sm text-slate-600 mb-4">
            Selecciona la razón principal por la cual se perdió este negocio.
          </p>

          {/* Reason Options - 3 Column Grid Layout */}
          <div className="grid grid-cols-3 gap-2.5 mb-5">
            {LOSS_REASONS.map((reason) => {
              const IconComponent = reason.icon;
              const colors = colorClasses[reason.color];
              const isSelected = selectedReason === reason.id;
              
              return (
                <label
                  key={reason.id}
                  className={`relative flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 cursor-pointer transition-all duration-200 text-center ${
                    isSelected
                      ? `${colors.border} ${colors.bg} shadow-md`
                      : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                  data-testid={`loss-reason-${reason.id}`}
                >
                  {isSelected && (
                    <div className={`absolute top-1.5 right-1.5 w-4 h-4 rounded-full flex items-center justify-center ${colors.icon}`}>
                      <Check className="w-2.5 h-2.5" />
                    </div>
                  )}
                  <div className={`p-2 rounded-lg ${isSelected ? colors.icon : 'bg-slate-100 text-slate-500'}`}>
                    <IconComponent className="w-5 h-5" />
                  </div>
                  <span className={`text-xs font-semibold leading-tight ${isSelected ? colors.text : 'text-slate-700'}`}>
                    {reason.label}
                  </span>
                  <input
                    type="radio"
                    name="lossReason"
                    value={reason.id}
                    checked={isSelected}
                    onChange={(e) => setSelectedReason(e.target.value)}
                    className="sr-only"
                  />
                </label>
              );
            })}
          </div>

          {/* Optional custom note */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Nota adicional <span className="font-normal text-slate-500">(opcional)</span>
            </label>
            <textarea
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
              placeholder="Detalles adicionales..."
              className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100 text-sm resize-none transition-all"
              rows={2}
              data-testid="loss-reason-custom-note"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-end gap-3">
          <button
            onClick={handleCancel}
            className="px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-200 rounded-xl transition-colors"
            data-testid="loss-reason-cancel-btn"
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedReason}
            className={`px-6 py-2.5 text-sm font-semibold text-white rounded-xl transition-all ${
              selectedReason
                ? 'bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 shadow-lg shadow-red-200'
                : 'bg-slate-300 cursor-not-allowed'
            }`}
            data-testid="loss-reason-confirm-btn"
          >
            Confirmar Pérdida
          </button>
        </div>
      </div>
    </div>
  );
};

export default LossReasonModal;
export { LOSS_REASONS };
